// ── The file viewer tab (Calem, report 24e8581a) ────────────────────────────────────────────────
// "Remove the X beside the paperclip and instead let me, as admin, delete when you launch the file
// in a new tab". A Job Documents paperclip used to hand the file to the browser's own file tab
// (api.openLibraryFile), which is the browser's window — nothing of ours can be drawn in it, so the
// only place an unmatch could live was an × on the pill itself, and that × is what stretched those
// pills into capsules of different widths. The clip opens this page instead: a tab of the app's own
// ("the clip can instead open the file in the [app's] tab of its own, with the file on screen and a
// delete button for admin"), the document drawn in the very pane Filing draws it in (PdfPreview).
//
// Delete = exactly what the × did (Calem, 2nd opening: "the delete button does what the X does
// today: unlink the document from the job. The file itself stays in Our Documents") — DELETE
// /jobs/:id/retro-docs/:docId, which the server already keeps to management. It is offered only
// when the clip said which job and which document it came from, and only to management: the same
// gate FileLibrary mirrors (server/src/routes/files.ts isManagement) — admin, or the 'management'
// role (Admin → Roles), never a name written in here. Everyone else sees the file and no button.
//
// Done, the tab closes itself and tells the tab it came from (window.opener) to reload its list,
// with the same 'slf:jobs-changed' event the status popups fire — the event does not cross tabs by
// itself, so it is dispatched on the opener's own window.
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useStore } from '../store';
import { brandOr } from '../utils/brand';
import { PdfPreview } from './PdfPreview';

// Same test FileLibrary previews a row with (canPreview): a PDF or a picture is drawn, anything
// else is offered as a download — this page never shows a broken image in place of a document.
const extOf = (name: string) => (name.toLowerCase().split('.').pop() || '');
const isPdf = (name: string) => extOf(name) === 'pdf';
const isImage = (name: string) => ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extOf(name));

export default function FileViewer() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [sp] = useSearchParams();
  const me = useStore(s => s.me);
  const jobId = sp.get('job') || '';
  const docId = sp.get('doc') || '';
  const name = sp.get('name') || '';
  const [url, setUrl] = useState('');
  const [mime, setMime] = useState('');   // read off the bytes, for a clip that passed no filename
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);   // unlinked, but the tab would not close itself
  const [err, setErr] = useState('');
  const urlRef = useRef('');

  // The file's bytes through the File Library's own door, which every staff login may download from.
  useEffect(() => {
    if (!id) return;
    let live = true;
    api.filesBlob(id)
      .then(async u => {
        if (!live) { URL.revokeObjectURL(u); return; }
        urlRef.current = u; setUrl(u);
        // The bytes are already in this tab, so reading their type back off the blob costs nothing and
        // answers for a file whose name never reached us.
        try { const b = await (await fetch(u)).blob(); if (live) setMime(b.type || ''); } catch { /* the name decides */ }
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, [id]);

  // The tab is named after the file, as the browser's own file tab was (api.openLibraryFile passes the
  // real filename for exactly that). DocumentTitle leaves /m/file/ alone so this is the only name set.
  useEffect(() => { document.title = name ? `${name} · ${t('fv_title')}` : t('fv_title'); }, [name, t]);

  const isManagement = me?.role === 'admin' || (me?.roles || []).includes('management');
  const canDelete = !!jobId && !!docId && isManagement && !gone;

  const remove = async () => {
    if (!window.confirm(t('fv_delete_confirm', { file: name || '—' }))) return;
    setBusy(true); setErr('');
    try {
      await api.jobRetroDocRemove(jobId, docId);
      // The Jobs list is in the tab that opened this one; its reload listener lives on that window, so
      // the event is built with the opener's own constructor and dispatched there. The detail carries
      // the file's name so the list can say what was unlinked once this tab is gone.
      const op: any = window.opener;
      try {
        if (op && !op.closed) op.dispatchEvent(new op.CustomEvent('slf:jobs-changed', { detail: { jobId, unlinked: name } }));
      } catch { /* the opener navigated away or is gone — the list reloads on its own next visit */ }
      setGone(true);
      window.close();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const body = failed ? (
    <div style={{ color: 'var(--text-tertiary)', fontSize: 13.5 }}>
      <i className="ti ti-file-alert" style={{ fontSize: 28, display: 'block', marginBottom: 8, textAlign: 'center' }} />{t('fl_preview_fail')}
    </div>
  ) : !url ? (
    <i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
  ) : (isPdf(name) || mime.includes('pdf')) ? (
    <PdfPreview src={url} />
  ) : (isImage(name) || mime.indexOf('image') === 0) ? (
    <img src={url} alt="" style={{ maxWidth: '100%', borderRadius: 6 }} />
  ) : (
    <button type="button" onClick={() => { api.filesDownload(id, name || 'file').catch((e: any) => window.alert(e.message)); }}
      style={{ padding: '9px 18px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
      <i className="ti ti-download" style={{ marginRight: 7 }} aria-hidden />{t('fl_download')}
    </button>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        <b style={{ fontSize: 14.5, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || t('fv_title')}</b>
        <div style={{ flex: 1 }} />
        {!!err && <span style={{ fontSize: 12.5, color: '#E24B4A' }}>{err}</span>}
        {/* The tab closes itself once the link is gone; a browser that refuses to close it says so here instead,
            with the same line the list behind it puts up as a toast. */}
        {gone && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('fv_unlinked', { file: name || '—' })}</span>}
        {canDelete && (
          <button type="button" onClick={remove} disabled={busy} title={t('fv_delete_tip')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 15px', borderRadius: 'var(--radius-md)', border: '1px solid rgba(226,75,74,0.45)', background: 'transparent', color: '#E24B4A', fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
            <i className={`ti ti-${busy ? 'loader' : 'trash'}`} style={{ fontSize: 15 }} aria-hidden />{t('fv_delete')}
          </button>
        )}
      </header>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 18 }}>
        <div style={{ width: '100%', maxWidth: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>{body}</div>
      </div>
    </div>
  );
}
